/* eslint-disable max-lines */
import { useNavigate } from 'react-router-dom';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useFormik } from 'formik';
import { useRecoilValue } from 'recoil';
import { useQueryClient } from '@tanstack/react-query';

import { createExternalInvoices } from 'api/Billing';
import { checkProductAvailability } from 'api/Products';

import useModal from 'lib/hooks/useModal';
import { formatDate } from 'lib/helpers/dateUtils';
import { appointmentKeys } from 'lib/queries/appointmentKeys';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import { useTerminal } from 'lib/hooks/queries/terminals/useTerminal';
import { hasModule, ia, iaRa, inRn, mString } from 'lib/helpers/utility';
import { overviewLedgerQueryKeys } from 'lib/constants/queries/queryKeys';

import { modulesState } from 'components/state';
import { showAlert } from 'components/shared/Alert/Alert';
import { currentPractice } from 'components/practice/practiceState';
import { getRecurringValidationError } from 'components/Payments/NewPayment/PaymentContent/components/RecurringPayments/lib/helpers';

import { fetchKioskReceipt } from '../lib/kioskReceipt';
import { AMOUNT_CHANGED_CODE } from '../lib/paymentErrors';
import { resolveKioskPaidAmount } from '../utils/planCharge';
import { releaseAbandonedKioskRequest } from '../lib/kioskPaymentRequest';
import { INVOICE_LINE_TYPES, ITEM_TYPES, itemQuantity, itemTotal } from '../lib/itemTypes';
import { PAYMENT_REFRESH_QUERY_KEYS, invalidateCatalogStock } from '../lib/paymentQueryKeys';
import {
  buildPacketLink,
  createCardPreAuthPacket,
  createPatientFormsPacket,
  packetPaymentPayload
} from '../utils/patientFormsPacket';
import {
  amountWithoutGratuity,
  isTechFeeMethod,
  isTiplessCheckout,
  resolveMaterializedAmounts,
  resolveTechFeeAmounts,
  toSurchargeRate
} from '../lib/checkoutAmounts';

import { getMutationFn } from './utils';

// Default to empty objects: the pre-auth form also renders outside the payment modal
// (instant packet, kiosk), where destructuring an undefined context used to throw.
export const PaymentContext = createContext({});
export const PaymentContextApi = createContext({});

const DEFAULT_SEND_INVOICE = { dueDate: null, memo: '' };
const DEFAULT_INVOICES = [];
const DEFAULT_TRANSACTION_TAGS = [];

export const CREATE_PATIENT_ALLOWED_METHODS = ['credit-card', 'ach'];

export const canCreatePatientFromCard = (method, processor) =>
  CREATE_PATIENT_ALLOWED_METHODS.includes(method) || (method === 'card' && processor === 'payroc');

export const PaymentContextProvider = ({
  children,
  amount,
  surchargeAmount = 0,
  initialMethod,
  initialMethodDetail = null,
  invoiceToken,
  patientId,
  // A patient picked or created inside the modal. Kept out of `initialValues` so choosing one
  // doesn't re-initialize the form and wipe the cart.
  guestPatientId,
  appointmentId,
  invoiceId,
  invoices = DEFAULT_INVOICES,
  selectAllInvoices = false,
  keepProvidedAmount = false,
  practiceId: practiceIdProp,
  onSuccess = () => {},
  onError = () => {},
  onCancel,
  checkin = false,
  recurringPayment = null,
  forceSaveCard = false,
  // Until the low-assurance policy resolves, card-on-file enforcement (and the methods it
  // removes) is unknown.
  isPolicyLoading = false,
  fromPlans = false,
  createPatientFromCCInfo = false,
  enabled = true,
  kioskWaitMinimized,
  onKioskWaitMinimizedChange,
  allowKioskMinimize = true,
  requirePatient = true
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const practice = useRecoilValue(currentPractice);
  const practiceModules = useRecoilValue(modulesState);
  const practiceId = practiceIdProp || practice.id;
  const techFeeEnabled = checkin || hasModule(practiceModules, 'medipay.technology_fee');
  const [postedResponse, setPostedResponse] = useState(null);
  const [kioskRequest, setKioskRequestState] = useState(null);
  const kioskRequestRef = useRef(null);
  const setKioskRequest = useCallback((request) => {
    kioskRequestRef.current = request;
    setKioskRequestState(request);
  }, []);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(
    () => () => {
      const kioskId = kioskRequestRef.current?.kioskId;
      if (!kioskId) return;
      kioskRequestRef.current = null;
      releaseAbandonedKioskRequest({ navigate: navigateRef.current, queryClient, kioskId });
    },
    [queryClient]
  );
  const [localKioskWaitMinimized, setLocalKioskWaitMinimized] = useState(false);
  const isKioskWaitControlled = kioskWaitMinimized !== undefined;
  const isKioskWaitMinimized = isKioskWaitControlled ? kioskWaitMinimized : localKioskWaitMinimized;
  const setIsKioskWaitMinimized = useCallback(
    (minimized) =>
      isKioskWaitControlled
        ? onKioskWaitMinimizedChange?.(minimized)
        : setLocalKioskWaitMinimized(minimized),
    [isKioskWaitControlled, onKioskWaitMinimizedChange]
  );
  const [kioskPreAuth, setKioskPreAuth] = useState(null);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResolvingKiosk, setIsResolvingKiosk] = useState(false);
  const isSubmittingRef = useRef(false);
  const formikValuesRef = useRef(null);
  const processPaymentRef = useRef(null);
  const processorRef = useRef(null);
  const terminalIdRef = useRef(null);
  const [selectedTerminal, setSelectedTerminal] = useState(null);

  const { isOpen, openModal, closeModal } = useModal(false);

  const guestPatientIdRef = useRef(guestPatientId);
  guestPatientIdRef.current = guestPatientId;

  const invoicesKey = useMemo(
    () => JSON.stringify(iaRa(invoices).map((invoice) => invoice?.id ?? invoice)),
    [invoices]
  );

  const stableInvoices = useMemo(() => iaRa(invoices), [invoicesKey]);

  const initialValues = useMemo(
    () => ({
      amount,
      appliedBalance: 0,
      method: initialMethod,
      methodDetail: initialMethodDetail,
      invoices: stableInvoices,
      appointmentId,
      invoiceId,
      surchargeAmount,
      patientId: patientId || guestPatientIdRef.current,
      payerId: null,
      invoiceToken,
      saveCard: forceSaveCard,
      paymentOption: null,
      surchargeEnabled:
        techFeeEnabled && !!practice?.surcharge_enabled && isTechFeeMethod(initialMethod),
      customPaymentAmounts: {},
      gratuity: 0,
      collectionMode: 'now',
      includedDocuments: [],
      patientFormIds: [],
      attachPreAuth: false,
      preAuthOption: null,
      recurringPayment,
      checkoutMode: recurringPayment ? 'recurring-plan' : 'pay-now',
      note: '',
      sendInvoice: DEFAULT_SEND_INVOICE,
      transactionTags: DEFAULT_TRANSACTION_TAGS,
      createAppointment: false,
      practitionerId: null,
      createPatientFromCCInfo
    }),
    [
      patientId,
      practiceId,
      appointmentId,
      invoiceId,
      invoiceToken,
      amount,
      surchargeAmount,
      stableInvoices,
      fromPlans,
      createPatientFromCCInfo,
      initialMethod,
      initialMethodDetail
    ]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true
  });

  formikValuesRef.current = formik.values;

  const resolvedPatientId = patientId || guestPatientId;

  // Only a change of guest writes the field: on mount, children may already have set it.
  const appliedGuestIdRef = useRef(guestPatientId);
  useEffect(() => {
    if (patientId || appliedGuestIdRef.current === guestPatientId) return;
    appliedGuestIdRef.current = guestPatientId;
    formik.setFieldValue('patientId', guestPatientId || null);
  }, [patientId, guestPatientId]);

  // Request-payment links and sent invoices are paid later by the patient, so no tip can ride on
  // them: switching into one drops the tip from the amount.
  const isTipless = isTiplessCheckout(formik.values);
  useEffect(() => {
    const gratuity = inRn(formik.values.gratuity);
    if (!isTipless || gratuity <= 0) return;
    formik.setFieldValue('amount', Math.max(0, inRn(formik.values.amount) - gratuity));
    formik.setFieldValue('gratuity', 0);
  }, [isTipless, formik.values.gratuity]);

  const { data: terminalData, isLoading: isTerminalLoading } = useTerminal({
    params: { practiceId, patientId: resolvedPatientId, invoiceToken, attribute: 'online' },
    dependencies: [practiceId, resolvedPatientId, invoiceToken],
    options: {
      enabled: enabled && !!practiceId
    }
  });

  const isKiosk = !!localStorage.getItem('myriadIsKiosk');

  useEffect(() => {
    // From Plans the staff picks the method in the grid (saved card / card present /
    // card not present), so nothing is pre-selected there.
    if (recurringPayment && !fromPlans) {
      formik.setFieldValue('method', 'credit-card');
    }
  }, [recurringPayment, fromPlans]);

  const lastIsTechFeeMethodRef = useRef(isTechFeeMethod(initialMethod));
  useEffect(() => {
    if (checkin || !techFeeEnabled || !practice?.surcharge_enabled) return;

    const values = formikValuesRef.current;
    if (!values?.method) return;

    const isMethodWithTechFee = isTechFeeMethod(values.method);
    if (lastIsTechFeeMethodRef.current === isMethodWithTechFee) return;
    lastIsTechFeeMethodRef.current = isMethodWithTechFee;
    if (!!values.surchargeEnabled === isMethodWithTechFee) return;

    const next = resolveTechFeeAmounts({
      values,
      isEnabled: isMethodWithTechFee,
      surchargePercentage: toSurchargeRate(practice?.surcharge_percentage)
    });
    formik.setFieldValue('surchargeEnabled', isMethodWithTechFee);
    formik.setFieldValue('surchargeAmount', next.surchargeAmount);
    formik.setFieldValue('amount', next.amount);
  }, [checkin, techFeeEnabled, practice?.surcharge_enabled, formik.values.method]);

  useEffect(() => {
    const defaultForCard =
      terminalData?.cardPresentTerminal || terminalData?.onlineTerminal || null;
    const defaultForOnline =
      terminalData?.onlineTerminal || terminalData?.cardPresentTerminal || null;

    setSelectedTerminal((current) => {
      const isCardMethod = formik.values.method === 'card';
      const defaultTerminal = isCardMethod ? defaultForCard : defaultForOnline;
      const currentValidForMethod = current && (isCardMethod ? !current.online : current.online);
      return currentValidForMethod ? current : defaultTerminal;
    });
  }, [terminalData?.onlineTerminal, terminalData?.cardPresentTerminal, formik.values.method]);

  const terminals = useMemo(
    () => iaRa(terminalData?.terminals).filter((t) => t.online),
    [terminalData?.terminals]
  );
  const cardPresentTerminals = useMemo(
    () =>
      iaRa(terminalData?.terminals).filter((t) => (t.processor === 'elavon' ? !t.online : true)),
    [terminalData?.terminals]
  );
  const terminalPin = selectedTerminal?.pin;
  const terminalId = selectedTerminal?.id;
  const processor = selectedTerminal?.processor;
  const isFullAppliedBalance = formik.values.appliedBalance > 0 && formik.values.amount <= 0;
  const appliedMethod = isFullAppliedBalance ? 'applied-balance' : formik.values.method;

  terminalIdRef.current = terminalId;

  processPaymentRef.current = getMutationFn(processor, appliedMethod);
  processorRef.current = processor;

  const invalidateQueryKey = [...PAYMENT_REFRESH_QUERY_KEYS, 'plans', ['cards', resolvedPatientId]];

  const { mutateAsync, isPending: isCharging } = useCustomMutation({
    // `methodOverride` lets a caller run a different method's handler than the one the form is
    // parked on — the pre-auth modal authorizes a stored card while `method` stays recurring-charge.
    mutationFn: async ({ methodOverride, attachPreAuth, ...params }) => {
      const processPayment = methodOverride
        ? getMutationFn(processorRef.current, methodOverride)
        : processPaymentRef.current;

      const result = await processPayment(navigate, params);

      if (!attachPreAuth || result?.code !== 0) return result;

      const cardId = result?.cardId || result?.card_id;
      const packet = cardId
        ? await createCardPreAuthPacket(navigate, {
            patientId: params.patientId,
            appointmentId: params.appointmentId,
            cardId
          }).catch(() => null)
        : null;

      if (!packet?.token) {
        showAlert({
          color: 'warning',
          title: "Payment processed, but the pre-auth form couldn't be created",
          message: 'Send it from the saved card on the patient profile.'
        });
        return result;
      }

      return { ...result, preAuthPacketToken: packet.token };
    },
    disableAlert: true,
    invalidateQueryKey,
    onSuccess: (params) => {
      const recurring = formikValuesRef.current?.recurringPayment;
      const method = formikValuesRef.current?.method;
      if (params?.awaitingKiosk) {
        setKioskRequest({
          kioskId: params?.kioskId,
          kioskName: params?.kioskName,
          patientId: params?.patientId,
          externalInvoiceToken: params?.externalInvoiceToken || null,
          isNothingDue: !!params?.isNothingDue
        });
        return;
      }
      if (recurring && method === 'request-payment' && !params?.instantPacketToken) {
        onError(params?.error || "Couldn't create the pre-authorization link", params?.code);
        return;
      }
      // `noPaymentSelected` also covers kiosk cash/check, which run the defer handler through
      // `methodOverride` while the form stays parked on the method the patient picked.
      if (method === 'defer-payment' || params?.noPaymentSelected) {
        onSuccess({ ...params, deferred: true, method: 'defer-payment' }, isFullAppliedBalance);
        return;
      }
      onSuccess(
        recurring
          ? {
              ...params,
              recurring: true,
              recurringType: recurring.type,
              recurringPayment: recurring,
              method,
              preAuthRequired: recurring.preAuthRequired !== false,
              patientId:
                formikValuesRef.current?.patientId ||
                resolvedPatientId ||
                params?.patient_id ||
                null,
              cardId: params?.card_id || params?.cardId || null,
              instantPacketToken: params?.instantPacketToken || null
            }
          : params,
        isFullAppliedBalance
      );
    },
    handleResponse: onError
  });

  const materializeCatalogItems = useCallback(async () => {
    const items = iaRa(formikValuesRef.current?.items);
    const catalogItems = items.filter((item) => item?.type !== ITEM_TYPES.INVOICE);
    if (!catalogItems.length) return { code: 0 };

    const targetPatientId = formikValuesRef.current?.patientId || resolvedPatientId || null;
    if (!targetPatientId && formikValuesRef.current?.createAppointment) {
      onError('Select a patient before creating an appointment for these items.', 1);
      return { code: 1 };
    }

    if (formikValuesRef.current?.createAppointment && !formikValuesRef.current?.practitionerId) {
      onError('Select a provider before creating an appointment for these items.', 1);
      return { code: 1 };
    }

    const priced = (item) => {
      const quantity = itemQuantity(item);
      const lineTotal = itemTotal(item);
      return {
        ...item,
        type: INVOICE_LINE_TYPES[item.type],
        quantity,
        sales_count: quantity,
        total_amount_cents: lineTotal,
        total_cost_cents: lineTotal
      };
    };

    const byType = (type) => catalogItems.filter((item) => item?.type === type).map(priced);
    const services = byType(ITEM_TYPES.SERVICE);
    const products = byType(ITEM_TYPES.PRODUCT);
    const packages = byType(ITEM_TYPES.PACKAGE);

    const productQuantities = [
      ...products.map((product) => ({ id: product.id, quantity: product.quantity })),
      ...packages.flatMap((pack) =>
        iaRa(pack?.products).map((product) => ({
          id: product?.id,
          quantity: (inRn(product?.quantity) || 1) * pack.quantity
        }))
      )
    ].reduce((acc, { id, quantity }) => {
      if (!id) return acc;
      return { ...acc, [id]: (acc[id] || 0) + quantity };
    }, {});

    const response = await checkProductAvailability(navigate, {
      // The endpoint only bills the catalog items (and creates the appointment) behind this flag.
      fromSell: true,
      createAppointment: !!formikValuesRef.current?.createAppointment,
      practitionerId: formikValuesRef.current?.practitionerId || null,
      invoice: {
        patient_id: targetPatientId,
        procedures: services,
        products,
        packages
      },
      products: productQuantities,
      procedures: [
        ...services.map((service) => service.id),
        ...packages.flatMap((pack) => iaRa(pack?.procedures).map((procedure) => procedure?.id))
      ].filter(Boolean)
    });

    const createdInvoices = iaRa(response?.invoices);

    if (response?.code !== 0 || !createdInvoices.length) {
      onError(response?.error || "Couldn't create an invoice for the selected items", 1);
      return { code: response?.code || 1 };
    }

    if (products.length || packages.length) invalidateCatalogStock(queryClient);

    const next = resolveMaterializedAmounts({
      values: formikValuesRef.current,
      createdInvoices,
      isSurchargeEnabled:
        !!formikValuesRef.current?.surchargeEnabled && !!practice?.surcharge_enabled,
      surchargePercentage: toSurchargeRate(practice?.surcharge_percentage)
    });

    formik.setFieldValue('items', next.items);
    formik.setFieldValue('invoices', next.invoices);
    formik.setFieldValue('amount', next.amount);
    formik.setFieldValue('surchargeAmount', next.surchargeAmount);
    formikValuesRef.current = {
      ...formikValuesRef.current,
      items: next.items,
      invoices: next.invoices,
      amount: next.amount,
      surchargeAmount: next.surchargeAmount
    };

    // The items are invoices now, so the next click charges `next.amount` without billing them
    // again — the staff just has to see it first.
    if (next.isAmountChanged) {
      onError(
        `The selected items were invoiced at ${mString(next.amount)}, not ${mString(next.shownAmount)}.`,
        AMOUNT_CHANGED_CODE
      );
      return { code: 1, materialized: true };
    }

    return { code: 0, invoices: next.invoices, materialized: true };
  }, [
    navigate,
    onError,
    queryClient,
    resolvedPatientId,
    formik.setFieldValue,
    practice?.surcharge_enabled,
    practice?.surcharge_percentage
  ]);

  // Billing the catalog items created invoices (and maybe an appointment) server side; when the
  // charge then fails, the patient's invoices and balances changed without a payment landing.
  const refreshAfterMaterialize = useCallback(() => {
    ['patientInvoices', 'patientBalance', 'unallocatedPatientBalance', ...overviewLedgerQueryKeys]
      .map((key) => [key])
      .concat([
        formikValuesRef.current?.createAppointment
          ? appointmentKeys.all
          : appointmentKeys.invoicesAll()
      ])
      .forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
  }, [queryClient]);

  const handlePayment = useCallback(
    async (data) => {
      // Billing the catalog items runs before the charge mutation reports pending, so without
      // this a double click bills (and charges) the cart twice.
      if (isSubmittingRef.current) return undefined;
      isSubmittingRef.current = true;
      setIsSubmitting(true);

      let didMaterialize = false;
      let isSettled = false;

      try {
        const materialized = await materializeCatalogItems();
        didMaterialize = !!materialized.materialized;
        if (materialized.code !== 0) return materialized;

        if (formikValuesRef.current?.checkoutMode === 'send-invoice') {
          const sendInvoiceData = formikValuesRef.current?.sendInvoice || {};
          const invoices = iaRa(formikValuesRef.current?.invoices);
          const total = invoices.reduce(
            (sum, inv) =>
              sum + Number(inv?.balance ?? inv?.total_amount_cents ?? inv?.amount_cents ?? 0),
            0
          );
          if (invoices.length === 0 || total <= 0) {
            onError('No invoices selected or total is zero', 1);
            return { code: 1 };
          }
          const internalDetails = invoices.map(({ id, balance }) => ({
            id,
            amount_paid: balance
          }));
          const surchargeAmount = inRn(formikValuesRef.current?.surchargeAmount);
          const surchargeEnabled = !!formikValuesRef.current?.surchargeEnabled;
          const effectiveSurcharge = surchargeEnabled ? surchargeAmount : 0;
          const externalInvoicePayload = {
            invoices: [
              {
                amount_cents: total,
                total_amount_cents: total + effectiveSurcharge,
                due_date:
                  sendInvoiceData.dueDate ||
                  formatDate(new Date(), practice?.timezone, 'MM/dd/yyyy'),
                memo: sendInvoiceData.memo || '',
                internal_invoice_ids: invoices.map((i) => i.id),
                patient: { id: formikValuesRef.current?.patientId || resolvedPatientId },
                tax_percentage: 0,
                surcharge_amount: effectiveSurcharge,
                aeob: false,
                is_detailed: false,
                type: 'kiosk',
                internal_details: internalDetails
              }
            ],
            skipConfirmation: true
          };

          setIsSendingInvoice(true);
          try {
            const result = await createExternalInvoices(navigate, externalInvoicePayload);
            if (result?.code !== 0) {
              onError(result?.error, result?.code);
              return result;
            }
            const externalInvoice = result?.invoices?.[0] || null;

            let packetLink = null;

            if (ia(formikValuesRef.current?.includedDocuments)) {
              const packet = await createPatientFormsPacket(navigate, {
                patientId: externalInvoice?.patient_id || formikValuesRef.current?.patientId,
                appointmentId: formikValuesRef.current?.appointmentId,
                includedDocuments: formikValuesRef.current?.includedDocuments,
                patientFormIds: formikValuesRef.current?.patientFormIds,
                payment: packetPaymentPayload({
                  amount: total + effectiveSurcharge,
                  surchargeAmount: effectiveSurcharge,
                  invoices
                })
              });

              if (!packet?.token) {
                onError("Couldn't attach the patient forms. Please try again.", 1);
                return { code: 1 };
              }

              packetLink = buildPacketLink(packet.token, { invoiceToken: externalInvoice?.token });
            }

            const sharedInvoice =
              externalInvoice && packetLink
                ? { ...externalInvoice, link: packetLink }
                : externalInvoice;

            isSettled = true;
            onSuccess({ sendInvoice: true, externalInvoice: sharedInvoice }, false);
            return { code: 0, externalInvoice: sharedInvoice };
          } finally {
            setIsSendingInvoice(false);
          }
        }

        const methodOverride = data?.methodOverride || null;
        const resolvedHandler = methodOverride
          ? getMutationFn(processorRef.current, methodOverride)
          : processPaymentRef.current;

        if (!resolvedHandler) {
          onError('Payment could not be processed. Please try again.', 1);
          return { code: 1 };
        }

        const finalValues = Object.fromEntries(
          Object.entries(formikValuesRef.current).filter(([_, v]) => v !== undefined && v !== null)
        );

        if (isTiplessCheckout(finalValues) && inRn(finalValues.gratuity) > 0) {
          finalValues.amount = amountWithoutGratuity(finalValues);
          finalValues.gratuity = 0;
        }

        const recurring = formikValuesRef.current?.recurringPayment;
        const fullAmount = Number(formikValuesRef.current?.amount) || 0;
        const upfrontOverride =
          recurring?.type === 'installments' && Number(recurring?.upfrontPayment) > 0
            ? Number(recurring.upfrontPayment)
            : null;

        // `recurringPayment.amount` is the gross plan total — the server subtracts
        // `upfrontPayment` itself when it schedules the installments, so taking it off here
        // too billed the patient's upfront twice against the plan.
        const recurringPayload = recurring
          ? {
              ...recurring,
              amount: Math.max(fullAmount, 0)
            }
          : recurring;

        const selectedMethod = formikValuesRef.current?.method;
        const methodSupportsCreatePatient = canCreatePatientFromCard(
          selectedMethod,
          processorRef.current
        );
        const requestedCreatePatientFromCC =
          data?.createPatientFromCCInfo ?? formikValuesRef.current?.createPatientFromCCInfo;
        const shouldCreatePatientFromCC =
          !formikValuesRef.current?.patientId && !!requestedCreatePatientFromCC;

        const canAttachToPatient =
          Boolean(finalValues.patientId) ||
          (methodSupportsCreatePatient && shouldCreatePatientFromCC);

        const attachCardPreAuth =
          selectedMethod === 'credit-card' &&
          !!finalValues.attachPreAuth &&
          !!finalValues.patientId &&
          !recurringPayload &&
          !methodOverride;

        const response = await mutateAsync({
          token: data?.token,
          ...finalValues,
          ...(recurringPayload && { recurringPayment: recurringPayload }),
          ...data,
          ...(upfrontOverride !== null && { amount: upfrontOverride }),
          // A plan charges the card again later, so paying only a portion up front still has
          // to store it — the server refuses to create a card plan without a saved card.
          ...(recurringPayload && selectedMethod !== 'saved-cards' && { saveCard: true }),
          createPatientFromCCInfo: methodSupportsCreatePatient && shouldCreatePatientFromCC,
          terminalId: terminalIdRef.current,
          practiceId,
          checkin,
          ...(forceSaveCard && { saveCard: true }),
          ...(!canAttachToPatient && { saveCard: false, recurringPayment: null }),
          ...(methodOverride && { methodOverride, method: methodOverride }),
          ...(attachCardPreAuth && { saveCard: true }),
          attachPreAuth: attachCardPreAuth
        });

        setPostedResponse(response);
        isSettled = response?.code === 0;

        return response;
      } catch (error) {
        if (error?.cancelled && onCancel) {
          onCancel();
          return;
        }
        console.error(error);
        // `retryData` lets the failure modal resubmit this same payment.
        onError({
          error: error?.message || error?.error || null,
          code: error?.code ?? 1,
          retryData: data ?? null
        });
      } finally {
        if (didMaterialize && !isSettled) refreshAfterMaterialize();
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    [
      mutateAsync,
      practiceId,
      checkin,
      onError,
      onCancel,
      onSuccess,
      materializeCatalogItems,
      refreshAfterMaterialize,
      resolvedPatientId,
      fromPlans,
      forceSaveCard
    ]
  );

  const togglePaymentMethod = useCallback(
    (key) => {
      const isSameMethod = formik.values.method === key;
      const nextMethod = isSameMethod ? null : key;
      formik.setFieldValue('methodDetail', null);
      formik.setFieldValue('method', nextMethod);

      const recurring = formik.values.recurringPayment;
      if (recurring && nextMethod === 'request-payment') {
        formik.setFieldValue('recurringPayment', { ...recurring, preAuthRequired: true });
      }

      closeModal();
    },
    [formik.values.method, formik.values.recurringPayment, formik.setFieldValue, closeModal]
  );

  const isLoading =
    isTerminalLoading || isCharging || isSendingInvoice || isSubmitting || isResolvingKiosk;
  const isAwaitingKiosk = !!kioskRequest;
  const isKioskMinimized = isAwaitingKiosk && isKioskWaitMinimized;

  useEffect(() => {
    if (!kioskRequest && isKioskWaitMinimized) setIsKioskWaitMinimized(false);
  }, [kioskRequest, isKioskWaitMinimized, setIsKioskWaitMinimized]);

  const clearKioskRequest = useCallback(() => setKioskRequest(null), [setKioskRequest]);

  // Bumped by the failure modal's "Try again": the checkout button re-runs its normal submit
  // (validation, card entry, cash confirmation) instead of replaying the failed request. It is
  // mounted twice (desktop and mobile layouts), so the first one to see a retry claims it.
  const [checkoutRetryCount, setCheckoutRetryCount] = useState(0);
  const pendingRetryRef = useRef(false);
  const requestCheckoutRetry = useCallback(() => {
    pendingRetryRef.current = true;
    setCheckoutRetryCount((count) => count + 1);
  }, []);
  const claimCheckoutRetry = useCallback(() => {
    if (!pendingRetryRef.current) return false;
    pendingRetryRef.current = false;
    return true;
  }, []);

  const resolveKioskPayment = useCallback(
    async (result) => {
      const plan = formikValuesRef.current?.recurringPayment;
      const kioskPatientId = kioskRequest?.patientId || resolvedPatientId || null;
      const kiosk = { kioskId: kioskRequest?.kioskId, kioskName: kioskRequest?.kioskName };
      setKioskRequest(null);

      if (result?.kind !== 'success') return false;

      if (plan) {
        // The card was taken at the kiosk, so the patient signs the pre-auth there too before
        // the plan is reported as created.
        setKioskPreAuth({
          ...kiosk,
          patientId: kioskPatientId,
          cardId: result?.cardId ?? null,
          planType: plan?.type || null,
          paidAmount: resolveKioskPaidAmount(plan, formikValuesRef.current),
          transactionId: result?.transactionId ?? null
        });
        return false;
      }

      setIsResolvingKiosk(true);
      const receipt = await fetchKioskReceipt(navigate, result?.transactionId);
      setIsResolvingKiosk(false);

      onSuccess(
        {
          kioskPayment: true,
          receipt,
          transactionId: result?.transactionId ?? null,
          patientId: kioskPatientId || receipt?.transaction?.patient_id || null
        },
        false
      );

      return !!receipt;
    },
    [
      onSuccess,
      setKioskRequest,
      navigate,
      resolvedPatientId,
      kioskRequest?.patientId,
      kioskRequest?.kioskId,
      kioskRequest?.kioskName
    ]
  );

  const finishKioskPreAuth = useCallback(
    ({ signed } = {}) => {
      const pending = kioskPreAuth;
      setKioskPreAuth(null);

      if (!pending) return;

      onSuccess(
        {
          recurring: true,
          recurringType: pending.planType,
          patientId: pending.patientId,
          cardId: pending.cardId,
          transactionId: pending.transactionId,
          paidAmount: pending.paidAmount,
          preAuthSigned: !!signed,
          preAuthRequired: !signed
        },
        false
      );
    },
    [kioskPreAuth, onSuccess]
  );

  const isOngoingTreatmentsPreAuth = formik.values.preAuthOption === 'ongoing-treatments';
  const isAmountLessThanAppliedBalance =
    !isOngoingTreatmentsPreAuth && formik.values.amount <= 0 && formik.values.appliedBalance <= 0;

  const isSendInvoice = formik.values.checkoutMode === 'send-invoice';
  const isRequestPayment = formik.values.method === 'request-payment';

  const isSendInvoiceEmpty = isSendInvoice && !ia(formik.values.invoices);
  const hasValidPaymentMethod = formik.values.method || isFullAppliedBalance || isSendInvoice;

  const isSavedCardWithoutDetail =
    formik.values.method === 'saved-cards' && !formik.values.methodDetail;

  const recurringValidationError = getRecurringValidationError(
    formik.values.recurringPayment,
    formik.values.amount
  );

  const isPlanWithoutPatient =
    (fromPlans || !!formik.values.recurringPayment) && !formik.values.patientId;

  const isAppointmentWithoutProvider =
    !!formik.values.createAppointment &&
    !!formik.values.patientId &&
    iaRa(formik.values.items).some((item) => item?.type !== ITEM_TYPES.INVOICE) &&
    !formik.values.practitionerId;

  const isDisabled =
    isPolicyLoading ||
    isAwaitingKiosk ||
    isPlanWithoutPatient ||
    isAppointmentWithoutProvider ||
    (isSendInvoice
      ? isLoading || isSendInvoiceEmpty
      : isRequestPayment
        ? isLoading ||
          !processPaymentRef.current ||
          isAmountLessThanAppliedBalance ||
          !!recurringValidationError
        : !hasValidPaymentMethod ||
          isLoading ||
          !processPaymentRef.current ||
          isAmountLessThanAppliedBalance ||
          isSavedCardWithoutDetail ||
          !!recurringValidationError);

  const disabledTooltip =
    (isPolicyLoading && 'Checking coverage for this visit…') ||
    (isAwaitingKiosk && 'Waiting for the kiosk payment to finish') ||
    (isPlanWithoutPatient && 'Select a patient before creating a plan') ||
    (isAppointmentWithoutProvider && 'Select a provider for the appointment') ||
    (isSendInvoiceEmpty && 'Add at least one invoice with a positive amount') ||
    (isAmountLessThanAppliedBalance && 'Amount must be greater than 0') ||
    (!isSendInvoice && !appliedMethod && 'Please select a payment method') ||
    (!isSendInvoice && !processPaymentRef.current && 'Payment method not supported') ||
    (isSavedCardWithoutDetail && 'Please select a saved card') ||
    recurringValidationError ||
    (isLoading && 'Processing payment…') ||
    '';

  const contextValue = useMemo(
    () => ({
      values: formik.values,
      isLoading,
      isDisabled,
      disabledTooltip,
      processor,
      terminalPin,
      terminalId,
      postedResponse,
      kioskRequest,
      kioskPreAuth,
      isAwaitingKiosk,
      isKioskMinimized,
      allowKioskMinimize,
      requirePatient,
      isSendingInvoice,
      isOpen,
      isTerminalLoading,
      isCharging,
      terminal: selectedTerminal,
      isFullAppliedBalance,
      practiceId,
      terminals,
      terminalData,
      isKiosk,
      cardPresentTerminals,
      forceSaveCard,
      fromPlans,
      selectAllInvoices,
      keepProvidedAmount,
      checkoutRetryCount,
      createPatientFromCCInfo: formik.values.createPatientFromCCInfo
    }),
    [
      formik.values,
      isLoading,
      isDisabled,
      disabledTooltip,
      processor,
      terminalPin,
      terminalId,
      postedResponse,
      kioskRequest,
      kioskPreAuth,
      isAwaitingKiosk,
      isKioskMinimized,
      allowKioskMinimize,
      requirePatient,
      isSendingInvoice,
      isOpen,
      isTerminalLoading,
      isCharging,
      selectedTerminal,
      isFullAppliedBalance,
      practiceId,
      terminals,
      terminalData,
      isKiosk,
      cardPresentTerminals,
      forceSaveCard,
      fromPlans,
      selectAllInvoices,
      keepProvidedAmount,
      checkoutRetryCount
    ]
  );

  const contextApi = useMemo(
    () => ({
      togglePaymentMethod,
      setFieldValue: formik.setFieldValue,
      handlePayment,
      openModal,
      closeModal,
      setSelectedTerminal,
      clearKioskRequest,
      resolveKioskPayment,
      setIsKioskWaitMinimized,
      finishKioskPreAuth,
      requestCheckoutRetry,
      claimCheckoutRetry
    }),
    [
      togglePaymentMethod,
      handlePayment,
      openModal,
      closeModal,
      setSelectedTerminal,
      clearKioskRequest,
      resolveKioskPayment,
      setIsKioskWaitMinimized,
      finishKioskPreAuth,
      requestCheckoutRetry,
      claimCheckoutRetry
    ]
  );

  return (
    <PaymentContext.Provider value={contextValue}>
      <PaymentContextApi.Provider value={contextApi}>{children}</PaymentContextApi.Provider>
    </PaymentContext.Provider>
  );
};

export const usePaymentContext = () => useContext(PaymentContext);
export const usePaymentContextApi = () => useContext(PaymentContextApi);
